import { useEffect, useRef } from "react";
import type { DocsApi } from "../../shared/api/endpoints";

const ANON_ID_KEY = "kb-anon-id";

/**
 * A stable per-browser id for anonymous readers.
 *
 * Lets the analytics screens count people rather than page loads without knowing
 * who anyone is. Stored in localStorage rather than a cookie so it is never sent
 * to the server automatically — it travels only in the read-event body, and only
 * for this app.
 */
function anonId(): string {
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
}

/**
 * Records that this article was opened, then how long it was read for.
 *
 * The duration goes out on `pagehide` rather than `beforeunload`: mobile Safari
 * frequently never fires `beforeunload` at all, which would lose the reading time
 * for a large share of readers.
 */
export function useReadTracking(api: DocsApi, articleSlug: string | undefined): void {
  const readEventId = useRef<number | null>(null);
  const openedAt = useRef<number>(0);

  useEffect(() => {
    if (!articleSlug) return;

    let cancelled = false;
    readEventId.current = null;
    openedAt.current = Date.now();

    api
      .recordRead(articleSlug, anonId())
      .then((result) => {
        // A null id means the server folded this into a recent read of the same
        // article — a refresh or a back-button, not a new visit.
        if (!cancelled) readEventId.current = result?.readEventId ?? null;
      })
      .catch(() => {
        // Analytics must never interrupt reading.
      });

    const report = () => {
      const id = readEventId.current;
      if (id === null) return;

      const seconds = Math.round((Date.now() - openedAt.current) / 1000);
      if (seconds < 3) return; // a glance, not a read

      api.reportDuration(id, seconds);
      // Cleared so navigating away and back does not report the same span twice.
      readEventId.current = null;
    };

    window.addEventListener("pagehide", report);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", report);
      // Also report on in-app navigation, which fires no page lifecycle event.
      report();
    };
  }, [api, articleSlug]);
}
